import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import { useAuthorizations } from 'hooks';
import { useHistory } from 'react-router-dom';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import { QuotationsStatus } from 'constants/defaultValues';
import { useReactiveVar } from '@apollo/client';
import { quotationsStatusVar } from 'features/insuranceDocument/variables';
import Replay from '@mui/icons-material/Replay';
import Add from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import CASCOCustomQuotationContainer from './customQuotation/CASCOCustomQuotationContainer';
import PropTypes from 'prop-types';
import { useListBrowserUtils } from '@ts-private/list-browser';
import { insuranceQuotationsStore } from './QuotationsListContainer';
import HistoryAnchorComponent from '../../HistoryAnchorComponent';
import {
  allowAddQuotationsVar,
  allowQuotationLockVar,
  customQuotationModalOpenVar,
  editingQuotationIdVar,
  hasHistoryVar
} from '../variables';

const QuotationsListActions = ({ documentId, insuranceTypeId, disableRecompute, recomputeQuotations }) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const status = useReactiveVar(quotationsStatusVar);
  const { t } = useTranslation();
  const history = useHistory();
  const { addActions } = useListBrowserUtils(insuranceQuotationsStore);

  const allowAddQuotations = useReactiveVar(allowAddQuotationsVar);
  const customQuotationModalOpen = useReactiveVar(customQuotationModalOpenVar);
  const editingQuotationId = useReactiveVar(editingQuotationIdVar);
  const hasHistory = useReactiveVar(hasHistoryVar);

  const computing = useMemo(() => status !== QuotationsStatus.VALID && status !== QuotationsStatus.EXPIRED, [status]);

  const authorizations = useAuthorizations(
    [
      AuthorizationCodes.hasRightToAddCascoQuotation,
      AuthorizationCodes.hasRightToUpdateCascoQuotationLock,
      AuthorizationCodes.hasRightToRecomputeQuotations,
      AuthorizationCodes.hasRightToViewOffersHistory
    ],
    documentTypeId,
    documentStateId
  );

  useEffect(() => {
    allowAddQuotationsVar(authorizations?.hasRightToAddCascoQuotation && !computing);
  }, [authorizations?.hasRightToAddCascoQuotation, computing]);

  useEffect(() => {
    allowQuotationLockVar(authorizations?.hasRightToUpdateCascoQuotationLock);
  }, [authorizations?.hasRightToUpdateCascoQuotationLock]);

  const handleEditCustomQuotation = useCallback(() => {
    editingQuotationIdVar(null);
    customQuotationModalOpenVar(true);
  }, []);

  const handleHistoryAnchorClick = useCallback(() => {
    history.push(`/insurance-documents/${documentId}/offers-history`);
  }, [history, documentId]);

  useEffect(() => {
    addActions([
      {
        name: 'RecomputeQuotations',
        label: t('Offer.Edit.Actions.RecomputeQuotations'),
        icon: <Replay />,
        handler: recomputeQuotations,
        disabled: disableRecompute,
        hidden: !authorizations?.hasRightToRecomputeQuotations
      },
      {
        name: 'AddCustomQuotation',
        label: t('Tooltips.Add'),
        icon: <Add />,
        handler: handleEditCustomQuotation,
        disabled: !allowAddQuotations
      }
    ]);
  }, [
    addActions,
    t,
    recomputeQuotations,
    disableRecompute,
    handleEditCustomQuotation,
    allowAddQuotations,
    authorizations?.hasRightToRecomputeQuotations
  ]);

  return (
    <>
      {customQuotationModalOpen && (
        <CASCOCustomQuotationContainer quotationId={editingQuotationId} insuranceTypeId={insuranceTypeId} documentId={documentId} />
      )}
      {hasHistory && authorizations?.hasRightToViewOffersHistory && <HistoryAnchorComponent onClick={handleHistoryAnchorClick} />}
    </>
  );
};

QuotationsListActions.propTypes = {
  documentId: PropTypes.number,
  insuranceTypeId: PropTypes.number,
  currentStateCode: PropTypes.string,
  disableRecompute: PropTypes.bool,
  recomputeQuotations: PropTypes.func
};

export default QuotationsListActions;
